import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";
import { MOBILE_NAV_MQ } from "./lobbyPageStyles";

const DIGIT_KEYS = ["m10", "m1", "s10", "s1"];
const DIGIT_DELAY_S = {
    m10: 0.18,
    m1: 0.13,
    s10: 0.08,
    s1: 0,
};
const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";
const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0];

function prefersReducedMotion() {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function DigitWindow({ digitKey, value, digitHeight }) {
    const stripRef = useRef(null);
    const currentRef = useRef(0);
    const reduceMotion = prefersReducedMotion();

    useEffect(() => {
        const strip = stripRef.current;
        if (!strip) return;

        const prev = currentRef.current;
        if (prev === value) return;
        currentRef.current = value;

        if (reduceMotion) {
            strip.style.transition = "none";
            strip.style.transform = `translateY(-${value * digitHeight}px)`;
            return;
        }

        const delay = DIGIT_DELAY_S[digitKey] || 0;
        strip.style.transition = `transform 0.5s ${EASE} ${delay}s`;

        if (prev === 9 && value === 0) {
            strip.style.transform = `translateY(-${10 * digitHeight}px)`;
            const onEnd = () => {
                strip.style.transition = "none";
                strip.style.transform = "translateY(0px)";
                strip.removeEventListener("transitionend", onEnd);
            };
            strip.addEventListener("transitionend", onEnd);
            return () => strip.removeEventListener("transitionend", onEnd);
        }

        strip.style.transform = `translateY(-${value * digitHeight}px)`;
    }, [value, digitKey, digitHeight, reduceMotion]);

    return (
        <Box
            sx={{
                width: { xs: "1.65rem", xl: "2.1rem" },
                height: `${digitHeight}px`,
                overflow: "hidden",
                position: "relative",
                [MOBILE_NAV_MQ]: {
                    width: "1.45rem",
                },
            }}
        >
            <Box
                ref={stripRef}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    willChange: "transform",
                    transform: "translateY(0px)",
                }}
            >
                {DIGITS.map((d, i) => (
                    <Box
                        // Extra trailing 0 needs a unique key
                        key={`${digitKey}-${i}-${d}`}
                        component="span"
                        sx={{
                            height: `${digitHeight}px`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontFamily: "'JetBrains Mono', monospace",
                            fontWeight: 500,
                            fontSize: { xs: "2.35rem", xl: "3rem" },
                            letterSpacing: "0.03em",
                            lineHeight: 1,
                            color: "rgba(242, 239, 232, 0.96)",
                            [MOBILE_NAV_MQ]: {
                                fontSize: "2rem",
                            },
                        }}
                    >
                        {d}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

DigitWindow.propTypes = {
    digitKey: PropTypes.oneOf(DIGIT_KEYS).isRequired,
    value: PropTypes.number.isRequired,
    digitHeight: PropTypes.number.isRequired,
};

/**
 * Cascading MM:SS timer driven by a unix start timestamp.
 */
export default function CascadeTimer({ timestamp }) {
    const [elapsed, setElapsed] = useState(0);
    const [digitHeight, setDigitHeight] = useState(() => {
        if (typeof window === "undefined" || !window.matchMedia) return 58;
        return window.matchMedia(MOBILE_NAV_MEDIA).matches ? 44 : 58;
    });

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const mq = window.matchMedia(MOBILE_NAV_MEDIA);
        const sync = () => setDigitHeight(mq.matches ? 44 : 58);
        sync();
        mq.addEventListener?.("change", sync);
        return () => mq.removeEventListener?.("change", sync);
    }, []);

    useEffect(() => {
        if (!timestamp) {
            setElapsed(0);
            return undefined;
        }
        const tick = () => {
            const nowUnix = Math.floor(Date.now() / 1000);
            setElapsed(Math.max(0, nowUnix - timestamp));
        };
        tick();
        const id = window.setInterval(tick, 1000);
        return () => window.clearInterval(id);
    }, [timestamp]);

    const mm = Math.floor(elapsed / 60)
        .toString()
        .padStart(2, "0");
    const ss = (elapsed % 60).toString().padStart(2, "0");
    const digits = {
        m10: Number(mm[0]),
        m1: Number(mm[1]),
        s10: Number(ss[0]),
        s1: Number(ss[1]),
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "1px",
                lineHeight: 1,
            }}
        >
            <DigitWindow
                digitKey="m10"
                value={digits.m10}
                digitHeight={digitHeight}
            />
            <DigitWindow
                digitKey="m1"
                value={digits.m1}
                digitHeight={digitHeight}
            />
            <Box
                component="span"
                sx={{
                    px: "3px",
                    opacity: 0.55,
                    transform: "translateY(-3px)",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 500,
                    fontSize: { xs: "2.35rem", xl: "3rem" },
                    color: "rgba(242, 239, 232, 0.96)",
                    [MOBILE_NAV_MQ]: {
                        fontSize: "2rem",
                    },
                }}
            >
                :
            </Box>
            <DigitWindow
                digitKey="s10"
                value={digits.s10}
                digitHeight={digitHeight}
            />
            <DigitWindow
                digitKey="s1"
                value={digits.s1}
                digitHeight={digitHeight}
            />
        </Box>
    );
}

CascadeTimer.propTypes = {
    timestamp: PropTypes.number,
};
